<script lang="ts">
    import { clickOutside } from "svelte-outside";
    import {
        cameraLayoutStore,
        getCameraLayoutLabel,
        getNextCameraLayout,
        type CameraLayoutMode,
    } from "../../../Stores/CameraLayoutStore";

    let layoutMenuOpen = $state(false);

    const layoutModes: CameraLayoutMode[] = [
        "automatic",
        "grid",
        "focus",
        "hidden",
    ];

    function closeLayoutMenu(): void {
        layoutMenuOpen = false;
    }

    function selectLayout(mode: CameraLayoutMode): void {
        cameraLayoutStore.set(mode);
        closeLayoutMenu();
    }

    function cycleLayout(): void {
        cameraLayoutStore.set(getNextCameraLayout($cameraLayoutStore));
        closeLayoutMenu();
    }
</script>

<section
    class="relative me-2 flex items-center rounded-lg bg-contrast/80 p-2 backdrop-blur pointer-events-auto"
    data-testid="camera-layout-control"
    use:clickOutside={closeLayoutMenu}
>
    <button
        type="button"
        class="flex h-12 w-12 items-center justify-center rounded-s-md text-white hover:bg-white/10 cursor-pointer @sm/actions:h-10 @sm/actions:w-10 @xl/actions:h-12 @xl/actions:w-12"
        class:bg-secondary={$cameraLayoutStore === "hidden"}
        title="{getCameraLayoutLabel($cameraLayoutStore)} — clique para mudar"
        aria-label="Mudar layout das câmeras"
        onclick={(event) => {
            event.stopPropagation();
            cycleLayout();
        }}
    >
        {#if $cameraLayoutStore === "grid"}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
        {:else if $cameraLayoutStore === "focus"}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="12" rx="2" />
                <path d="M7 20h10" />
                <path d="M12 16v4" />
            </svg>
        {:else if $cameraLayoutStore === "hidden"}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3l18 18" />
                <path d="M10.6 10.7a2 2 0 002.7 2.7" />
                <path d="M9.9 4.2A10.5 10.5 0 0112 4c5 0 9 4 10 8a12.7 12.7 0 01-2 3.8" />
                <path d="M6.6 6.6A12.3 12.3 0 002 12c1 4 5 8 10 8a10.6 10.6 0 005.4-1.5" />
            </svg>
        {:else}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 7V4h3" />
                <path d="M17 4h3v3" />
                <path d="M20 17v3h-3" />
                <path d="M7 20H4v-3" />
                <rect x="8" y="8" width="8" height="8" rx="1" />
            </svg>
        {/if}
    </button>

    <div class="h-8 w-px bg-white/20"></div>

    <button
        type="button"
        class="flex h-12 w-7 items-center justify-center rounded-e-md text-white hover:bg-white/10 cursor-pointer @sm/actions:h-10 @xl/actions:h-12"
        class:bg-secondary={layoutMenuOpen}
        title="Escolher layout"
        aria-label="Escolher layout das câmeras"
        aria-expanded={layoutMenuOpen}
        onclick={(event) => {
            event.stopPropagation();
            layoutMenuOpen = !layoutMenuOpen;
        }}
    >
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class:rotate-180={layoutMenuOpen}
            class="transition-transform"
        >
            <path d="M6 9l6 6 6-6" />
        </svg>
    </button>

    {#if layoutMenuOpen}
        <div
            class="absolute start-1/2 top-full z-[500] mt-2 min-w-[220px] -translate-x-1/2 rounded-lg bg-contrast/95 p-2 text-white shadow-xl backdrop-blur pointer-events-auto"
            data-testid="camera-layout-selector"
        >
            <div class="px-3 pb-2 pt-1 text-xs font-bold uppercase opacity-70">
                Layout das câmeras
            </div>

            {#each layoutModes as mode}
                <button
                    type="button"
                    class="mb-1 flex w-full items-center rounded-md px-3 py-2 text-left text-sm font-semibold hover:bg-white/10 cursor-pointer"
                    class:bg-secondary={$cameraLayoutStore === mode}
                    aria-pressed={$cameraLayoutStore === mode}
                    onclick={(event) => {
                        event.stopPropagation();
                        selectLayout(mode);
                    }}
                >
                    {getCameraLayoutLabel(mode)}
                </button>
            {/each}
        </div>
    {/if}
</section>
